import type { Edge, Node } from "@xyflow/react";

import type { WorkspaceEdge, WorkspaceNode } from "../../../types/workspace";
import {
  workspaceChildActionsForNode,
  type WorkspaceChildAction,
  type WorkspaceChildActionKind,
  type WorkspaceChildAddMode,
} from "./workspaceChildActions";
import {
  WORKSPACE_ACCOUNT_CARD_SIZE,
  WORKSPACE_GRAPH_COLUMN_GAP,
  WORKSPACE_PLACEHOLDER_NODE_SIZE,
  snapWorkspaceNodeByCenter,
} from "./workspaceGraphConstants";
import { workspaceNodeShowsForwardPlaceholder } from "./workspacePlaceholderPolicy";
import {
  computePlaceholderNodePosition,
  resolveWorkspaceNodePosition,
} from "./workspaceLayoutUtils";
import {
  computeExecutingAccountNodePosition,
  isWorkspaceExecutingAccountNodeId,
  listPolicyExecutingAccounts,
  type WorkspaceExecutingAccountNodeData,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
  WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
} from "./workspaceExecutingAccountUtils";

export const WORKSPACE_PLACEHOLDER_NODE_TYPE = "workspacePlaceholder";
export const WORKSPACE_PLACEHOLDER_EDGE_TYPE = "workspacePlaceholder";
export const WORKSPACE_INSERT_NODE_TYPE = "workspaceInsert";
export const WORKSPACE_INSERT_NODE_SIZE = 44;
/** Inscribed square so a 45° rotation fits the outer insert bounds. */
export const WORKSPACE_INSERT_NODE_INNER_SIZE = Math.round(
  WORKSPACE_INSERT_NODE_SIZE / Math.SQRT2
);

export type WorkspacePlaceholderNodeData = {
  parentNode: WorkspaceNode;
  actions: WorkspaceChildAction[];
  label: string;
  mutating?: boolean;
  showActionsMenu?: boolean;
  onToggleMenu?: () => void;
  onAddChild?: (
    node: WorkspaceNode,
    kind: WorkspaceChildActionKind,
    mode?: WorkspaceChildAddMode
  ) => void;
  /** Combined policy forward diamond — opens the combined simulation run flow. */
  onRunCombinedSimulation?: (node: WorkspaceNode) => void;
};

export type WorkspaceInsertNodeData = {
  accountNode: WorkspaceNode;
  /** Direct Account→Report children that fan out from this shared insert hub. */
  reportNodes: WorkspaceNode[];
  mutating?: boolean;
  showActionsMenu?: boolean;
  onToggleMenu?: () => void;
  onAddStrategy?: (
    accountNode: WorkspaceNode,
    reportNode: WorkspaceNode,
    mode?: WorkspaceChildAddMode
  ) => void;
  onAddReport?: (accountNode: WorkspaceNode, mode?: WorkspaceChildAddMode) => void;
};

function placeholderNodeId(parentNodeId: string): string {
  return `placeholder:${parentNodeId}`;
}

function placeholderEdgeId(parentNodeId: string): string {
  return `placeholder-edge:${parentNodeId}`;
}

/** One insert hub per account that has direct report children. */
function insertNodeId(accountNodeId: string): string {
  return `insert:${accountNodeId}`;
}

function insertInboundEdgeId(accountNodeId: string): string {
  return `insert-edge-in:${accountNodeId}`;
}

function insertOutboundEdgeId(accountNodeId: string, reportNodeId: string): string {
  return `insert-edge-out:${accountNodeId}:${reportNodeId}`;
}

function uniqueChildKinds(actions: WorkspaceChildAction[]): WorkspaceChildActionKind[] {
  const seen = new Set<WorkspaceChildActionKind>();
  const kinds: WorkspaceChildActionKind[] = [];
  for (const action of actions) {
    if (seen.has(action.kind)) continue;
    seen.add(action.kind);
    kinds.push(action.kind);
  }
  return kinds;
}

function shouldShowPlaceholder(
  parent: WorkspaceNode,
  _actions: WorkspaceChildAction[],
  edges: WorkspaceEdge[],
  nodes: WorkspaceNode[],
  maxSimulationsPerReport?: number
): boolean {
  return workspaceNodeShowsForwardPlaceholder(parent, edges, nodes, {
    maxSimulationsPerReport,
  });
}

function placeholderLabel(
  parent: WorkspaceNode,
  actions: WorkspaceChildAction[]
): string {
  if (parent.type === "combined_policy") return "Run combined simulation";
  const kinds = uniqueChildKinds(actions);
  if (kinds.length === 1) {
    const kind = kinds[0]!;
    if (kind === "strategy") return "Add strategy";
    if (kind === "report") return "Add report";
    if (kind === "simulation") return "Add simulation";
  }
  return "Add next step";
}

export function computeInsertStrategyNodePosition(
  accountNode: WorkspaceNode,
  reportNodes: WorkspaceNode | WorkspaceNode[]
): { x: number; y: number } {
  const reports = (Array.isArray(reportNodes) ? reportNodes : [reportNodes]).filter(Boolean);
  const accountPosition = resolveWorkspaceNodePosition(accountNode);
  const accountRight = accountPosition.x + WORKSPACE_ACCOUNT_CARD_SIZE;
  const accountCenterY = accountPosition.y + WORKSPACE_ACCOUNT_CARD_SIZE / 2;
  const size = { width: WORKSPACE_INSERT_NODE_SIZE, height: WORKSPACE_INSERT_NODE_SIZE };

  if (reports.length === 0) {
    // Exact account spine Y — keep the account→insert edge horizontal.
    const x =
      accountRight + WORKSPACE_GRAPH_COLUMN_GAP / 2 - WORKSPACE_INSERT_NODE_SIZE / 2;
    const snappedX = snapWorkspaceNodeByCenter(
      { x, y: accountCenterY - WORKSPACE_INSERT_NODE_SIZE / 2 },
      size
    ).x;
    return { x: snappedX, y: accountCenterY - WORKSPACE_INSERT_NODE_SIZE / 2 };
  }

  // Sit in the gap between the account's right edge and the leftmost report.
  // Keep the account→insert edge horizontal: Y tracks the account spine (layout
  // already recenters the account on its direct reports).
  const reportLeft = Math.min(
    ...reports.map((report) => resolveWorkspaceNodePosition(report).x)
  );

  const x = (accountRight + reportLeft) / 2 - WORKSPACE_INSERT_NODE_SIZE / 2;
  const y = accountCenterY - WORKSPACE_INSERT_NODE_SIZE / 2;
  const snappedX = snapWorkspaceNodeByCenter({ x, y }, size).x;
  return { x: snappedX, y };
}

function accountReportEdges(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): Array<{ edge: WorkspaceEdge; accountNode: WorkspaceNode; reportNode: WorkspaceNode }> {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));
  return edges.flatMap((edge) => {
    const source = nodeById.get(edge.sourceNodeId);
    const target = nodeById.get(edge.targetNodeId);
    if (!source || !target) return [];
    if (source.type !== "account" || target.type !== "report") return [];
    if (source.isDisabled || target.isDisabled) return [];
    return [{ edge, accountNode: source, reportNode: target }];
  });
}

/** Group direct Account→Report links into one insert hub per account. */
function accountReportBranches(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[]
): Array<{ accountNode: WorkspaceNode; reportNodes: WorkspaceNode[] }> {
  const byAccount = new Map<string, { accountNode: WorkspaceNode; reportNodes: WorkspaceNode[] }>();

  for (const { accountNode, reportNode } of accountReportEdges(nodes, edges)) {
    const existing = byAccount.get(accountNode.id);
    if (existing) {
      if (!existing.reportNodes.some((node) => node.id === reportNode.id)) {
        existing.reportNodes.push(reportNode);
      }
      continue;
    }
    byAccount.set(accountNode.id, { accountNode, reportNodes: [reportNode] });
  }

  return Array.from(byAccount.values());
}

export function toPlaceholderFlowNodes(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  options?: {
    mutating?: boolean;
    actionsMenuNodeId?: string | null;
    maxSimulationsPerReport?: number;
    onToggleMenu?: (nodeId: string) => void;
    onAddChild?: (
      node: WorkspaceNode,
      kind: WorkspaceChildActionKind,
      mode?: WorkspaceChildAddMode
    ) => void;
    onRunCombinedSimulation?: (node: WorkspaceNode) => void;
  }
): Array<Node<WorkspacePlaceholderNodeData>> {
  return nodes.flatMap((parent) => {
    const actions = workspaceChildActionsForNode(parent);
    if (!shouldShowPlaceholder(parent, actions, edges, nodes, options?.maxSimulationsPerReport)) {
      return [];
    }
    const id = placeholderNodeId(parent.id);

    return [
      {
        id,
        type: WORKSPACE_PLACEHOLDER_NODE_TYPE,
        position: computePlaceholderNodePosition(
          parent,
          edges,
          WORKSPACE_PLACEHOLDER_NODE_SIZE,
          nodes
        ),
        width: WORKSPACE_PLACEHOLDER_NODE_SIZE,
        height: WORKSPACE_PLACEHOLDER_NODE_SIZE,
        draggable: false,
        selectable: false,
        connectable: false,
        focusable: false,
        data: {
          parentNode: parent,
          actions,
          label: placeholderLabel(parent, actions),
          mutating: options?.mutating,
          showActionsMenu: options?.actionsMenuNodeId === id,
          onToggleMenu: () => options?.onToggleMenu?.(id),
          onAddChild: options?.onAddChild,
          onRunCombinedSimulation: options?.onRunCombinedSimulation,
        },
      },
    ];
  });
}

export function toInsertStrategyFlowNodes(
  nodes: WorkspaceNode[],
  edges: WorkspaceEdge[],
  options?: {
    mutating?: boolean;
    actionsMenuNodeId?: string | null;
    onToggleMenu?: (nodeId: string) => void;
    onAddStrategy?: (
      accountNode: WorkspaceNode,
      reportNode: WorkspaceNode,
      mode?: WorkspaceChildAddMode
    ) => void;
    onAddReport?: (accountNode: WorkspaceNode, mode?: WorkspaceChildAddMode) => void;
  }
): Array<Node<WorkspaceInsertNodeData>> {
  return accountReportBranches(nodes, edges).map(({ accountNode, reportNodes }) => {
    const id = insertNodeId(accountNode.id);
    return {
      id,
      type: WORKSPACE_INSERT_NODE_TYPE,
      position: computeInsertStrategyNodePosition(accountNode, reportNodes),
      width: WORKSPACE_INSERT_NODE_SIZE,
      height: WORKSPACE_INSERT_NODE_SIZE,
      draggable: false,
      selectable: false,
      connectable: false,
      focusable: false,
      zIndex: 5,
      data: {
        accountNode,
        reportNodes,
        mutating: options?.mutating,
        showActionsMenu: options?.actionsMenuNodeId === id,
        onToggleMenu: () => options?.onToggleMenu?.(id),
        onAddStrategy: options?.onAddStrategy,
        onAddReport: options?.onAddReport,
      },
    };
  });
}

export function toPlaceholderFlowEdges(nodes: WorkspaceNode[], edges: WorkspaceEdge[]): Edge[] {
  const nodeById = new Map(nodes.map((node) => [node.id, node]));

  return nodes.flatMap((parent) => {
    const actions = workspaceChildActionsForNode(parent);
    if (!shouldShowPlaceholder(parent, actions, edges, nodes)) return [];

    const nextKind =
      parent.type === "strategy"
        ? "report"
        : parent.type === "report"
          ? "simulation"
          : parent.type === "combined_policy"
            ? "combined_simulation"
            : null;
    const siblingNodes = nextKind
      ? edges
          .filter((edge) => edge.sourceNodeId === parent.id)
          .map((edge) => nodeById.get(edge.targetNodeId))
          .filter((node): node is WorkspaceNode => node != null && node.type === nextKind)
      : [];
    const siblingNodeIds = siblingNodes.map((node) => node.id);
    // Park the placeholder vertical after the last real sibling lane.
    const lane = siblingNodeIds.length;
    const laneCount = siblingNodeIds.length + 1;

    return [
      {
        id: placeholderEdgeId(parent.id),
        source: parent.id,
        target: placeholderNodeId(parent.id),
        sourceHandle: "source",
        targetHandle: "target",
        type: WORKSPACE_PLACEHOLDER_EDGE_TYPE,
        selectable: false,
        focusable: false,
        data: {
          siblingNodeIds,
          lane,
          laneCount,
        },
      },
    ];
  });
}

export function toInsertStrategyFlowEdges(nodes: WorkspaceNode[], edges: WorkspaceEdge[]): Edge[] {
  return accountReportBranches(nodes, edges).flatMap(({ accountNode, reportNodes }) => {
    const insertId = insertNodeId(accountNode.id);
    return [
      {
        id: insertInboundEdgeId(accountNode.id),
        source: accountNode.id,
        target: insertId,
        sourceHandle: "source",
        targetHandle: "target",
        type: "workspacePipeline",
        selectable: false,
        focusable: false,
      },
      ...reportNodes.map((reportNode) => ({
        id: insertOutboundEdgeId(accountNode.id, reportNode.id),
        source: insertId,
        target: reportNode.id,
        sourceHandle: "source",
        targetHandle: "target",
        type: "workspacePipeline",
        selectable: false,
        focusable: false,
      })),
    ];
  });
}

/** Real Account→Report edges that are replaced visually by insert-node segments. */
export function isAccountReportEdgeSkipped(
  edge: WorkspaceEdge,
  nodes: WorkspaceNode[]
): boolean {
  return accountReportEdges(nodes, [edge]).length > 0;
}

export function isWorkspacePlaceholderNodeId(nodeId: string): boolean {
  return nodeId.startsWith("placeholder:");
}

export function isWorkspaceInsertNodeId(nodeId: string): boolean {
  return nodeId.startsWith("insert:");
}

export function isWorkspaceEphemeralNodeId(nodeId: string): boolean {
  return (
    isWorkspacePlaceholderNodeId(nodeId) ||
    isWorkspaceInsertNodeId(nodeId) ||
    isWorkspaceExecutingAccountNodeId(nodeId)
  );
}

export function syncPlaceholderPositionsToParents(
  nodes: Array<Node>,
  edges: WorkspaceEdge[]
): Array<Node> {
  const parentById = new Map(
    nodes
      .filter((node) => !isWorkspaceEphemeralNodeId(node.id))
      .map((node) => [node.id, node])
  );

  const liveWorkspaceNodes: WorkspaceNode[] = Array.from(parentById.values()).flatMap(
    (flowNode) => {
      const workspaceNode = (flowNode.data as { workspaceNode?: WorkspaceNode } | undefined)
        ?.workspaceNode;
      if (!workspaceNode) return [];
      return [{ ...workspaceNode, position: flowNode.position }];
    }
  );

  return nodes.map((node) => {
    if (isWorkspacePlaceholderNodeId(node.id)) {
      const data = node.data as WorkspacePlaceholderNodeData;
      const parentFlowNode = parentById.get(data.parentNode.id);
      if (!parentFlowNode) return node;

      const parentWithLivePosition: WorkspaceNode = {
        ...data.parentNode,
        position: parentFlowNode.position,
      };

      const siblingNodes = liveWorkspaceNodes.map((candidate) =>
        candidate.id === parentWithLivePosition.id ? parentWithLivePosition : candidate
      );

      return {
        ...node,
        position: computePlaceholderNodePosition(
          parentWithLivePosition,
          edges,
          WORKSPACE_PLACEHOLDER_NODE_SIZE,
          siblingNodes
        ),
        data: {
          ...data,
          parentNode: parentWithLivePosition,
        },
      };
    }

    if (isWorkspaceInsertNodeId(node.id)) {
      const data = node.data as WorkspaceInsertNodeData;
      const accountFlow = parentById.get(data.accountNode.id);
      if (!accountFlow) return node;

      const reportNodesWithLivePosition = data.reportNodes.flatMap((reportNode) => {
        const reportFlow = parentById.get(reportNode.id);
        if (!reportFlow) return [];
        return [
          {
            ...reportNode,
            position: reportFlow.position,
          },
        ];
      });
      if (reportNodesWithLivePosition.length === 0) return node;

      const accountWithLivePosition: WorkspaceNode = {
        ...data.accountNode,
        position: accountFlow.position,
      };

      return {
        ...node,
        position: computeInsertStrategyNodePosition(
          accountWithLivePosition,
          reportNodesWithLivePosition
        ),
        data: {
          ...data,
          accountNode: accountWithLivePosition,
          reportNodes: reportNodesWithLivePosition,
        },
      };
    }

    if (isWorkspaceExecutingAccountNodeId(node.id)) {
      const data = node.data as WorkspaceExecutingAccountNodeData;
      const parentFlowNode = parentById.get(data.parentNode.id);
      if (!parentFlowNode) return node;

      const parentWithLivePosition: WorkspaceNode = {
        ...data.parentNode,
        position: parentFlowNode.position,
      };
      const siblingNodes = liveWorkspaceNodes.map((candidate) =>
        candidate.id === parentWithLivePosition.id ? parentWithLivePosition : candidate
      );
      const accounts = listPolicyExecutingAccounts(parentWithLivePosition);
      const accountIndex = accounts.findIndex(
        (account) =>
          account.brokerName === data.account.brokerName &&
          account.brokerAccountCode === data.account.brokerAccountCode
      );
      if (accountIndex < 0) return node;

      return {
        ...node,
        position: computeExecutingAccountNodePosition(
          parentWithLivePosition,
          accountIndex,
          accounts.length,
          edges,
          siblingNodes
        ),
        width: WORKSPACE_EXECUTING_ACCOUNT_NODE_WIDTH,
        height: WORKSPACE_EXECUTING_ACCOUNT_NODE_HEIGHT,
        data: {
          ...data,
          parentNode: parentWithLivePosition,
        },
      };
    }

    return node;
  });
}
